import {
  Dialog,
  DialogContent,
  Skeleton,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { queryData } from '@/app/api/mock/projects/[projectId]/db/mongoMockData';
import { NoSqlDBResourceTypes } from '@/app/api/mock/projects/[projectId]/inventory/inventoryData';
import {
  getMockData,
  getMockHeaders,
} from '@/app/api/mock/v1/projects/[projectId]/queries/[queryId]/results/mockData';
import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import { PanelHeader } from '@/components/panels/panelHeader';
import { Link } from '@/components/shared/link';
import { useDAL } from '@/contexts/useDAL';

import QueryResultsTable from './QueryResultsTable';
import QueryResultsTree from './QueryResultsTree';
import ToggleButtons from './ToggleButtons';

interface TableMockDataPreviewDialogProps {
  serverId: string;
  tableId: string;
  totalRowCount: number;
  actionButton?: { disabled?: boolean; href: string; text: string };
  onClose: () => void;
}

export default function TableMockDataPreviewDialog(
  props: TableMockDataPreviewDialogProps
) {
  const dal = useDAL();
  const { body: resource, isLoading: isLoadingResource } = dal.inventory.get(
    props.serverId
  );
  const [displayMode, setDisplayMode] = useState<'tree' | 'table'>('table');
  const [isNoSql, setIsNoSql] = useState(false);
  const [data, setData] = useState<
    | {
        headers: string[];
        rows: string[][];
      }
    | undefined
  >();

  useEffect(() => {
    if (resource?.resource.resourceType) {
      if (
        [
          ...Object.values(NoSqlDBResourceTypes),
          ResourceType.AtlasMongodbCluster,
        ].includes(resource.resource.resourceType) ||
        resource.resource.classifications?.appsDetails?.apps?.some(
          (x) => x.name === 'MongoDB'
        )
      ) {
        setIsNoSql(true);
        setDisplayMode('tree');
        setData({ headers: queryData.columns, rows: queryData.records });
      } else {
        setData({
          headers: getMockHeaders(props.tableId),
          rows: getMockData(props.tableId),
        });
      }
    }
  }, [resource, props.serverId, props.tableId]);

  return (
    <Dialog
      open={true}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '100%',
          height: '100%',
        },
      }}
      onClose={props.onClose}
    >
      <PanelHeader onClose={props.onClose} title={props.tableId} />
      {isLoadingResource && (
        <DialogContent>
          <Stack
            className='h-full text-center'
            justifyContent='center'
            alignItems='center'
          >
            <Typography variant='body1'>
              Please wait while we process your request.
            </Typography>
            <Typography variant='body1'>
              This operation could potentially require a significant amount of
              time.
            </Typography>
            <Skeleton width='200px' className='mt-[16px]' />
          </Stack>
        </DialogContent>
      )}
      {!isLoadingResource && (
        <>
          <DialogContent>
            <Stack gap='24px'>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant='body1'>
                  {`Showing a preview of the first ${data?.rows.length} rows out of ${props.totalRowCount.toLocaleString()} records.`}
                </Typography>
                {props.actionButton && (
                  <Link
                    disabled={props.actionButton.disabled}
                    href={props.actionButton.href}
                  >
                    <ButtonWithIcon
                      disabled={props.actionButton.disabled}
                      variant='outlined'
                      text={props.actionButton.text}
                      icon='material-symbols-frame-inspect-rounded'
                      iconPosition='left'
                    />
                  </Link>
                )}
              </Stack>
              <Box className='relative'>
                {isNoSql && (
                  <ToggleButtons
                    value={displayMode}
                    onChange={setDisplayMode}
                    items={[
                      {
                        title: 'Tree',
                        iconClass: 'material-symbols-data-object',
                        value: 'tree',
                      },
                      {
                        title: 'Table',
                        iconClass: 'material-symbols-table',
                        value: 'table',
                      },
                    ]}
                  />
                )}
                {displayMode === 'tree' ? (
                  <QueryResultsTree
                    loading={false}
                    headerColumns={data?.headers}
                    data={data?.rows}
                  />
                ) : (
                  <QueryResultsTable
                    loading={false}
                    headerColumns={data?.headers}
                    data={data?.rows}
                  />
                )}
              </Box>
            </Stack>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export function TableDataPreviewDialog({
  data,
  onClose,
  isLoading,
}: {
  data: { headers: string[]; rows: string[][] };
  onClose: () => void;
  isLoading: boolean;
}) {
  return (
    <Dialog
      open={true}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '100%',
          height: '100%',
        },
      }}
      onClose={onClose}
    >
      <PanelHeader onClose={onClose} title='Preview sample records' />
      <>
        <DialogContent>
          <Stack gap='24px'>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='body1'>
                {`Showing a sample of ${data?.rows.length} records.`}
              </Typography>
            </Stack>
            <Box className='relative'>
              <QueryResultsTable
                loading={isLoading}
                headerColumns={data?.headers}
                data={data?.rows}
              />
            </Box>
          </Stack>
        </DialogContent>
      </>
    </Dialog>
  );
}
